import { useMemo, useState } from 'react';
import TextBox from 'devextreme-react/text-box';
import TextArea from 'devextreme-react/text-area';
import Button from 'devextreme-react/button';
import { newToast } from '../../../components';

interface TranslationRow {
  key: number;
  result: string;
  translation: string;
}

/**
 * Estructura que espera el backend en Rga_Formula para el tipo ResultadosTraducciones:
 *   [{Result:'p.yel', Translation: 'AMARILLO CLARO'}, {Result:'other', Translation: 'OTRO'}]
 * No es JSON estricto (claves sin comillas, comillas simples), por eso se parsea/serializa a mano.
 */
const PAIR_REGEX = /Result\s*:\s*['"]([^'"]*)['"]\s*,\s*Translation\s*:\s*['"]([^'"]*)['"]/g;

// Las comillas romperían el formato de serialización, así que siempre se quitan.
const stripQuotes = (value: string) => (value ?? '').replace(/['"]/g, '');

// El Result debe coincidir EXACTAMENTE con lo que envía el analizador, y algunos
// mandan el valor con espacios al inicio/final (ej. 'Neg '). Por eso NO se recorta:
// si se recortara, la comparación en el backend fallaría y no se traduciría.
const sanitizeResult = (value: string) => stripQuotes(value);

// La traducción es el texto que sale al informe: ahí los espacios de borde no aportan.
const sanitizeTranslation = (value: string) => stripQuotes(value).trim();

// Una fila cuenta como vacía si no tiene nada más que espacios.
const hasContent = (value: string) => stripQuotes(value).trim() !== '';

/** true si el valor tiene contenido real pero además espacios al inicio o al final. */
export const hasEdgeSpaces = (value: string) => {
  const v = value ?? '';
  return v.trim() !== '' && v !== v.trim();
};

// Contador global de keys: cada fila creada (inicial, importada o agregada) recibe una
// key única para evitar que React reutilice instancias del TextBox de DevExtreme, lo que
// dejaría valores obsoletos en el widget.
let ROW_SEED = 1000;
const nextKey = () => ROW_SEED++;

export const parseTranslations = (raw: string | null | undefined): TranslationRow[] => {
  if (!raw) return [];
  const rows: TranslationRow[] = [];
  let match: RegExpExecArray | null;
  PAIR_REGEX.lastIndex = 0;
  while ((match = PAIR_REGEX.exec(raw)) !== null) {
    rows.push({ key: nextKey(), result: match[1], translation: match[2] });
  }
  return rows;
};

export const serializeTranslations = (rows: TranslationRow[]): string => {
  const body = rows
    .filter((r) => hasContent(r.result) || hasContent(r.translation))
    .map((r) => `{Result:'${sanitizeResult(r.result)}', Translation: '${sanitizeTranslation(r.translation)}'}`)
    .join(', ');
  return `[${body}]`;
};

interface TranslationsBuilderProps {
  value: string;
  onValueChange: (value: string) => void;
}

export const TranslationsBuilder = ({ value, onValueChange }: TranslationsBuilderProps) => {
  const [rows, setRows] = useState<TranslationRow[]>(() => {
    const parsed = parseTranslations(value);
    return parsed.length ? parsed : [{ key: nextKey(), result: '', translation: '' }];
  });
  const [showImport, setShowImport] = useState(false);
  const [importText, setImportText] = useState('');

  const json = useMemo(() => serializeTranslations(rows), [rows]);

  // Los espacios en los bordes son intencionales cuando el analizador los envía,
  // pero también son un error de tipeo frecuente. Se cuentan para avisar y ofrecer limpiarlos.
  const spacedCount = useMemo(() => rows.filter((r) => hasEdgeSpaces(r.result)).length, [rows]);

  const commit = (next: TranslationRow[]) => {
    setRows(next);
    onValueChange(serializeTranslations(next));
  };

  const updateRow = (key: number, field: 'result' | 'translation', val: string) => {
    commit(rows.map((r) => (r.key === key ? { ...r, [field]: val } : r)));
  };

  const addRow = () => commit([...rows, { key: nextKey(), result: '', translation: '' }]);

  const trimAllResults = () => {
    // nextKey() fuerza a React a remontar el TextBox: DevExtreme conserva el valor
    // anterior en el widget si sólo cambia la prop.
    commit(rows.map((r) => (hasEdgeSpaces(r.result) ? { ...r, key: nextKey(), result: r.result.trim() } : r)));
    newToast('SUCCESS', 'Se quitaron los espacios de los resultados', 3);
  };

  const removeRow = (key: number) => {
    const next = rows.filter((r) => r.key !== key);
    commit(next.length ? next : [{ key: nextKey(), result: '', translation: '' }]);
  };

  const copyJson = async () => {
    try {
      await navigator.clipboard.writeText(json);
      newToast('SUCCESS', 'JSON de traducciones copiado al portapapeles', 3);
    } catch {
      // Fallback para navegadores/contextos sin Clipboard API
      const ta = document.createElement('textarea');
      ta.value = json;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
        newToast('SUCCESS', 'JSON de traducciones copiado al portapapeles', 3);
      } catch {
        newToast('ERROR', 'No se pudo copiar el JSON', 4);
      }
      document.body.removeChild(ta);
    }
  };

  const applyImport = () => {
    const parsed = parseTranslations(importText);
    if (!parsed.length) {
      newToast('WARNING', 'No se encontraron traducciones válidas en el texto pegado', 4);
      return;
    }
    commit(parsed);
    setImportText('');
    setShowImport(false);
    newToast('SUCCESS', `Se importaron ${parsed.length} traducciones`, 3);
  };

  return (
    <div className='translations-builder'>
      <div className='tb-toolbar'>
        <span className='tb-count'>{rows.filter((r) => hasContent(r.result) || hasContent(r.translation)).length} traducciones</span>
        <div className='tb-toolbar-actions'>
          {spacedCount > 0 && <Button icon='clearformat' text='Quitar espacios' stylingMode='text' hint='Recorta los espacios al inicio y al final de los resultados' onClick={trimAllResults} />}
          <Button icon='copy' text='Copiar JSON' stylingMode='text' type='default' onClick={copyJson} />
          <Button icon='paste' text='Importar' stylingMode='text' onClick={() => setShowImport((v) => !v)} />
        </div>
      </div>

      {showImport && (
        <div className='tb-import'>
          <TextArea
            value={importText}
            onValueChanged={(e) => setImportText(e.value)}
            height={90}
            placeholder="Pega aquí el JSON de otro analito, ej: [{Result:'red', Translation: 'ROJA'}, ...]"
          />
          <div className='tb-import-actions'>
            <Button text='Cargar' type='default' stylingMode='contained' onClick={applyImport} />
            <Button
              text='Cancelar'
              stylingMode='text'
              onClick={() => {
                setShowImport(false);
                setImportText('');
              }}
            />
          </div>
        </div>
      )}

      {spacedCount > 0 && (
        <div className='tb-space-hint'>
          <b>␣</b> {spacedCount === 1 ? '1 resultado tiene' : `${spacedCount} resultados tienen`} espacios al inicio o al final. Se guardan tal cual, porque el analizador puede enviarlos así (ej. <code>'Neg '</code>). Si no son
          intencionales, usa <b>Quitar espacios</b>.
        </div>
      )}

      <div className='tb-header'>
        <span>RESULTADO (ENTRA)</span>
        <span>TRADUCCIÓN (SALE)</span>
        <span />
      </div>

      <div className='tb-rows'>
        {rows.map((row) => (
          <div className='tb-row' key={row.key}>
            <div className='tb-result'>
              {/* valueChangeEvent='input': por defecto DevExtreme sólo reporta el valor en el blur.
                  Si se hace clic directo en Guardar, el grid serializa la fila antes de que React
                  aplique el setState del blur y se pierde la última edición. */}
              <TextBox
                value={row.result}
                valueChangeEvent='input'
                placeholder='p.yel'
                onValueChanged={(e) => updateRow(row.key, 'result', e.value)}
              />
              {hasEdgeSpaces(row.result) && (
                <span className='tb-space-badge' title={`Este resultado tiene espacios al inicio o al final: "${row.result}". Se conservan tal cual porque el analizador puede enviarlos así.`}>
                  ␣
                </span>
              )}
            </div>
            <span className='tb-arrow'>→</span>
            <TextBox
              value={row.translation}
              valueChangeEvent='input'
              placeholder='AMARILLO CLARO'
              onValueChanged={(e) => updateRow(row.key, 'translation', e.value)}
            />
            <Button icon='trash' stylingMode='text' type='default' hint='Eliminar' onClick={() => removeRow(row.key)} />
          </div>
        ))}
      </div>

      <Button className='tb-add' icon='plus' text='Agregar traducción' stylingMode='text' onClick={addRow} />

      <details className='tb-preview'>
        <summary>Ver JSON generado</summary>
        <code>{json}</code>
      </details>
    </div>
  );
};
